/**
 * Rôles d'un participant dans son équipe.
 *
 * Un rôle peut être marqué « par défaut » : tout participant le possède, sans qu'il soit
 * jamais inscrit dans `team.roles`. Ce choix est délibéré — l'attribution est déduite à
 * la lecture plutôt qu'écrite dans chaque équipe :
 *
 *  - un participant qui rejoint en cours de partie l'obtient immédiatement, sans qu'une
 *    quelconque écriture ait à être déclenchée ;
 *  - personne ne peut le lui retirer, puisqu'il n'y a rien à retirer ;
 *  - changer de rôle par défaut ne laisse aucune trace à nettoyer dans les équipes.
 *
 * La contrepartie est que toute lecture des rôles doit passer par ici.
 */

/** Nom du rôle par défaut, ou `null` s'il n'y en a pas. */
export function defaultRoleName(roleDefinitions) {
  const definitions = Array.isArray(roleDefinitions) ? roleDefinitions : []
  const found = definitions.find(role => role && role.isDefault)
  return found ? found.name : null
}

/**
 * Rôles d'un participant : ceux qui lui ont été attribués, plus le rôle par défaut.
 *
 * @param {object} team équipe, portant `roles` indexé par identifiant de participant
 * @param {string} userId participant concerné
 * @param {object[]} roleDefinitions `parameters.roles` de l'application
 * @returns {string[]} noms de rôles, sans doublon
 */
export function rolesOf(team, userId, roleDefinitions) {
  const assigned = team && team.roles && Array.isArray(team.roles[userId])
    ? team.roles[userId]
    : []

  const fallback = defaultRoleName(roleDefinitions)
  if (!fallback || assigned.includes(fallback)) return [...assigned]

  // En tête : c'est le socle commun, les rôles choisis viennent le compléter.
  return [fallback, ...assigned]
}

/** Ce rôle est-il celui attribué d'office à tout le monde ? */
export function isDefaultRole(roleName, roleDefinitions) {
  const fallback = defaultRoleName(roleDefinitions)
  return Boolean(fallback) && fallback === roleName
}

/**
 * Marque un rôle comme celui par défaut, et retire la marque aux autres.
 * Repasser le rôle déjà marqué le laisse sans rôle par défaut.
 */
export function setDefaultRole(roleDefinitions, roleName) {
  const definitions = Array.isArray(roleDefinitions) ? roleDefinitions : []
  const wasDefault = defaultRoleName(definitions) === roleName

  for (const role of definitions) {
    if (!role) continue
    role.isDefault = !wasDefault && role.name === roleName
  }
  return definitions
}

export default { defaultRoleName, rolesOf, isDefaultRole, setDefaultRole }
